import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme } from './lightTheme';
import { darkTheme } from './darkTheme';
import { Theme } from './theme';
import { GlobalStyles } from './GlobalStyles';

export type ThemeMode = 'auto' | 'light' | 'dark';

interface ThemeContextType {
    themeMode: ThemeMode;
    setThemeMode: (mode: ThemeMode) => void;
    theme: Theme;
}

const defaultContext: ThemeContextType = {
    themeMode: 'auto',
    setThemeMode: () => { /* пустая функция по умолчанию */ },
    theme: lightTheme
};

const ThemeContext = createContext<ThemeContextType>(defaultContext);

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
    children: ReactNode;
}

function useThemeLogic() {
    const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
        const savedTheme = localStorage.getItem('themeMode');
        return (savedTheme as ThemeMode) || 'auto';
    });

    const [systemIsDark, setSystemIsDark] = useState(() => 
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const handleChange = (e: MediaQueryListEvent) => {
            setSystemIsDark(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const handleThemeModeChange = useCallback((mode: ThemeMode) => {
        setThemeMode(mode);
        localStorage.setItem('themeMode', mode);
    }, []);

    const isDarkTheme = themeMode === 'dark' || (themeMode === 'auto' && systemIsDark);
    const theme = isDarkTheme ? darkTheme : lightTheme;

    return {
        themeMode,
        setThemeMode: handleThemeModeChange,
        theme
    };
}

export const ThemeProvider: React.FC<ThemeProviderProps> = React.memo(({ children }) => {
    const themeState = useThemeLogic();

    return React.createElement(
        ThemeContext.Provider,
        { value: themeState },
        React.createElement(
            StyledThemeProvider,
            { theme: themeState.theme },
            [
                children,
                React.createElement(GlobalStyles, { theme: themeState.theme, key: 'global-styles' })
            ]
        )
    );
});
